import {
  AlertDescription,
  AlertTitle,
  Button,
  chakra,
  Link,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Stack,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import { WarningAlert } from '~components/ui/Alert';
import { ExternalLinkIcon } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { useFeatureFlag } from '~utils/features';

interface Props {
  onCancel: () => void;
}

export function StepSubscription({ onCancel }: Props) {
  const navigate = useNavigate();
  const { verifyUserPermissions, organization } = useAuth();
  const { isEnabled: isAppDirectMarketplaceLinkEnabled } = useFeatureFlag(
    'appDirectMarketplaceLink',
  );

  const canManageSubscriptions = verifyUserPermissions([Permission.WaveSubscriptionUpdate]);

  const goToSubscriptionsSettings = () =>
    navigate(`/${organization?.handle}/settings/subscriptions`);

  return (
    <>
      <ModalHeader>Add more seats</ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      <ModalBody>
        <WarningAlert
          actionButton={
            canManageSubscriptions && isAppDirectMarketplaceLinkEnabled ? (
              <Link
                href={process.env.REACT_APP_APPDIRECT_MARKETPLACE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                marginLeft="8"
                textDecoration="none"
              >
                <Button variant="outline" size="sm" colorScheme="orange" color="orange.700">
                  <Stack direction="row">
                    <chakra.span textDecoration="none">Visit ProStore</chakra.span>
                    <ExternalLinkIcon />
                  </Stack>
                </Button>
              </Link>
            ) : undefined
          }
        >
          <AlertTitle>More seats needed to claim this display</AlertTitle>
          <AlertDescription>
            {canManageSubscriptions
              ? 'This customer is currently out of seats. To claim this display, buy more seats or select a different subscription below.'
              : 'The amount of available seats is used. To claim a new display contact an admin to purchase more seats on ProStore or to select a different subscription plan.'}
          </AlertDescription>
        </WarningAlert>
      </ModalBody>
      <ModalFooter>
        <Stack flex="1" direction="row" spacing="3" justifyContent="flex-end">
          <Button variant="ghost" colorScheme="blue" onClick={onCancel}>
            Cancel
          </Button>
          {canManageSubscriptions ? (
            <Button variant="solid" colorScheme="blue" onClick={goToSubscriptionsSettings}>
              Manage subscriptions
            </Button>
          ) : (
            <Button variant="solid" colorScheme="blue" isDisabled>
              Next
            </Button>
          )}
        </Stack>
      </ModalFooter>
    </>
  );
}
